/**
 * Drawer Component
 *
 * A right-side slide-over panel built on Radix UI Dialog.
 * Mirrors Modal's prop shape, but anchors to the viewport edge instead of centering.
 */

import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/utils/cn';

// ============================================
// Types
// ============================================

export interface DrawerProps {
  /** Drawer open state */
  open: boolean;
  /** Open state change handler */
  onOpenChange: (open: boolean) => void;
  /** Drawer title */
  title?: string;
  /** Drawer description */
  description?: string;
  /**
   * Custom header content, replacing the default title/description block.
   * Must render a `DrawerTitle` for accessibility.
   */
  header?: React.ReactNode;
  /** Drawer content */
  children: React.ReactNode;
  /** Drawer width */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Side the drawer slides in from */
  side?: 'right' | 'left';
  /** Show close button */
  showCloseButton?: boolean;
  /** Close on overlay click */
  closeOnOverlayClick?: boolean;
  /** Additional content class */
  className?: string;
  /** Footer content */
  footer?: React.ReactNode;
}

// ============================================
// Size Classes
// ============================================

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-3xl',
};

// ============================================
// Drawer Component
// ============================================

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  header,
  children,
  size = 'lg',
  side = 'right',
  showCloseButton = true,
  closeOnOverlayClick = true,
  className,
  footer,
}: DrawerProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="modal-overlay fixed inset-0 z-50 bg-black/50"
          onClick={closeOnOverlayClick ? undefined : (e) => e.preventDefault()}
        />
        <Dialog.Content
          className={cn(
            'drawer-content fixed inset-y-0 z-50 flex h-full w-full flex-col',
            'border-border dark:border-border-dark',
            'bg-background dark:bg-background-dark shadow-2xl',
            'focus:outline-none',
            side === 'right' ? 'right-0 border-l sm:rounded-l-2xl' : 'left-0 border-r sm:rounded-r-2xl',
            sizeClasses[size],
            className
          )}
          data-side={side}
          onPointerDownOutside={closeOnOverlayClick ? undefined : (e) => e.preventDefault()}
        >
          {/* Header */}
          {(title || header || showCloseButton) && (
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border p-5 dark:border-border-dark">
              {header ?? (
                <div className="space-y-1">
                  {title && (
                    <Dialog.Title className="text-lg font-semibold text-foreground dark:text-foreground-dark">
                      {title}
                    </Dialog.Title>
                  )}
                  {description && (
                    <Dialog.Description className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                      {description}
                    </Dialog.Description>
                  )}
                </div>
              )}
              {showCloseButton && (
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className={cn(
                      'shrink-0 rounded-lg p-1.5 transition-colors',
                      'text-foreground-muted dark:text-foreground-dark-muted',
                      'hover:bg-secondary-100 hover:text-foreground',
                      'dark:hover:bg-secondary-800 dark:hover:text-foreground-dark',
                      'focus:outline-none focus:ring-2 focus:ring-primary'
                    )}
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              )}
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-surface/60 p-4 dark:border-border-dark dark:bg-secondary-900/30">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export const DrawerClose = Dialog.Close;
export const DrawerTitle = Dialog.Title;
export const DrawerDescription = Dialog.Description;
